import { StorageKey, StorageKind, StorageSubscribe } from '@revelio/core';

import { savePreferencesForScope } from '../api/user-preferences-api';
import {
  PreferenceScope,
  SELF_MANAGED_PREFERENCE_KEYS,
  type UserPreferenceKey,
  type UserPreferences,
} from '../types';
import { getDefaultPreferences } from './preference-defaults';
import { filterPreferencesForScope } from './preference-helpers';
import { PreferenceBrowserStorage } from './preference-session-storage';

// Broadcast key to notify other hook instances that a successful server sync occurred
export const getPreferenceSavedSyncKey = (scope: PreferenceScope) =>
  `${StorageKey.UserPreferencesPrefix}${scope}-saved-sync`;

/**
 * Saves the currently visible preferences as the new defaults.
 * This function:
 * 1. Extracts only visible preferences from the effective UI preferences
 * 2. Merges them into local storage
 * 3. Persists only visible keys to the server
 * 4. Refreshes saved preferences and notifies listeners
 */
export async function saveVisiblePreferencesAsDefault(
  affectedScope: PreferenceScope,
  effectivePreferences: UserPreferences,
  // Only the keys that actually changed (the modal's diff) are persisted — an
  // untouched setting is left absent from the record so it falls back to the
  // coded default on read, instead of writing a redundant explicit default.
  keysToSave: UserPreferenceKey[],
  refetchSavedPreferences: () => Promise<void>,
  taxonomyVersions?: { role?: string; skill?: string; activity?: string }
): Promise<void> {
  // Extract only the changed keys, skipping self-managed ones (own save UX)
  const visibleEffectiveEntries = Object.entries(effectivePreferences).filter(
    ([key]) =>
      keysToSave.includes(key as UserPreferenceKey) &&
      !SELF_MANAGED_PREFERENCE_KEYS.includes(key as UserPreferenceKey)
  );
  const visibleEffective = Object.fromEntries(
    visibleEffectiveEntries
  ) as Partial<UserPreferences>;

  if (Object.keys(visibleEffective).length === 0) {
    // Nothing changed — leave the record untouched (untouched keys keep the
    // coded default). Still refresh so listeners settle.
    await refetchSavedPreferences();
    return;
  }

  const globalStored = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
  const currentStored = PreferenceBrowserStorage.get(affectedScope);

  // Stamp the taxonomy versions the saved defaults were captured under, so a
  // later taxonomy bump can detect stale defaults on load. This must run on
  // every save that persists versioned defaults — including a default-filters-
  // only save — not just when the taxonomy choice itself changes: reconcile on
  // read discards saved defaults whose version stamp is missing, so an unstamped
  // default-filters save would read back as stale and revert to the coded
  // default. Merge onto the already-stored taxonomies so recording the version
  // never clobbers the user's (possibly untouched, hence unsaved) choice.
  // `effective*TaxonomySelection` is a company-page adaptive RESOLUTION, not a
  // user choice — strip it so the saved record carries only the raw choice
  // (mirrors the editor's on-change clear and the migration's stripAdaptiveResidue).
  if (
    taxonomyVersions &&
    (visibleEffective.taxonomies || visibleEffective.defaultFilters)
  ) {
    const {
      effectiveRoleTaxonomySelection: _role,
      effectiveSkillTaxonomySelection: _skill,
      effectiveActivityTaxonomySelection: _activity,
      ...taxonomiesWithoutResidue
    } = visibleEffective.taxonomies ?? globalStored.taxonomies ?? {};
    visibleEffective.taxonomies = {
      ...taxonomiesWithoutResidue,
      ...(taxonomyVersions.role
        ? { roleTaxonomyVersion: taxonomyVersions.role }
        : {}),
      ...(taxonomyVersions.skill
        ? { skillTaxonomyVersion: taxonomyVersions.skill }
        : {}),
      ...(taxonomyVersions.activity
        ? { activityTaxonomyVersion: taxonomyVersions.activity }
        : {}),
    };
  }

  // Merge visible preferences into local storage
  const mergedStored = {
    ...getDefaultPreferences(affectedScope),
    ...globalStored,
    ...currentStored,
    ...visibleEffective,
  } as UserPreferences;

  PreferenceBrowserStorage.set(affectedScope, mergedStored);
  PreferenceBrowserStorage.set(PreferenceScope.GLOBAL, mergedStored);

  // Persist only visible preferences to server. When the affected scope IS
  // GLOBAL (e.g. the homepage preferences modal), the scoped and global saves
  // resolve to the same record — skip the scoped write so we don't create-then-
  // update the GLOBAL record in two round trips.
  if (affectedScope !== PreferenceScope.GLOBAL) {
    const affectedToSave = filterPreferencesForScope(
      affectedScope,
      visibleEffective
    );
    if (Object.keys(affectedToSave).length > 0) {
      await savePreferencesForScope(affectedScope, affectedToSave);
    }
  }

  const globalToSave = filterPreferencesForScope(
    PreferenceScope.GLOBAL,
    visibleEffective
  );
  if (Object.keys(globalToSave).length > 0) {
    await savePreferencesForScope(PreferenceScope.GLOBAL, globalToSave);
  }

  // Refresh saved preferences immediately
  await refetchSavedPreferences();

  // Notify other instances to refresh
  StorageSubscribe.notify(
    StorageKind.Session,
    getPreferenceSavedSyncKey(affectedScope)
  );
}

/**
 * Restores visible preferences to their default values.
 * This function:
 * 1. Uses saved defaults if available, otherwise falls back to hard defaults
 * 2. Merges only visible keys with current stored values
 * 3. Updates local storage and returns the applied defaults
 */
export function restoreVisiblePreferencesToDefaults(
  affectedScope: PreferenceScope,
  savedDefaults: UserPreferences,
  visiblePreferenceKeys: UserPreferenceKey[]
): { appliedDefaults: UserPreferences; mergedPreferences: UserPreferences } {
  // Prefer saved defaults if present, otherwise use hard defaults
  const effectiveDefaults =
    savedDefaults && Object.keys(savedDefaults).length > 0
      ? savedDefaults
      : getDefaultPreferences(affectedScope);

  // Start with current stored values merged with hard defaults
  const globalStored = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
  const currentStored = PreferenceBrowserStorage.get(affectedScope);
  const mergedAll = {
    ...getDefaultPreferences(affectedScope),
    ...globalStored,
    ...currentStored,
  } as UserPreferences;

  // Overwrite only visible keys with defaults, skipping self-managed ones
  visiblePreferenceKeys.forEach((key) => {
    if (SELF_MANAGED_PREFERENCE_KEYS.includes(key)) return;
    mergedAll[key] = effectiveDefaults[key as keyof UserPreferences] as never;
  });

  // Write merged preferences
  PreferenceBrowserStorage.set(affectedScope, mergedAll);
  PreferenceBrowserStorage.set(PreferenceScope.GLOBAL, mergedAll);

  return {
    appliedDefaults: effectiveDefaults,
    mergedPreferences: mergedAll,
  };
}
